import React, { useState, useEffect, useRef } from 'react';
import * as S from './style';
import { Header } from "../Header";
import { apiPetro, headers } from "../../services/api";
import { Resultado } from "../../models/base/basic";
import { Loading } from "../Loading";
import { Error } from "../Error";
import { formatDateToBR } from '../../utils';

interface DadosBasicosCliente {
  codigo: string;
  loja: string;
  cnpj: string;
  razaoSocial: string;
  inscricaoEstadual: string;
  ultimaCompra: string;
  compraTotal: number;
  titulosPagos: number;
  titulosAtrasados12Meses: number;
  clienteHaAnos: number;
  clienteHaMeses: number;
  codigo_assessor: string;
  nomeAssessor: string;
}

interface FaturamentoMes {
  mes: string;
  ano: string;
  faturamento: string;
}

interface FormData {
  codigoCliente: string;
  codigoGrupoEconomico: string; // Campo editável pelo usuário para buscar dados
  razaoSocial: string;
  cnpjCpf: string;
  inscricaoEstadual: string;
  tipoPropriedade: string;
  tempoAtividade: string;
  codigoAssessor: string; // Preenchido automaticamente com o codigo_assessor da API
  nomeAssessor: string;
  ultimaCompra: string;
  compraTotal: number;
  titulosPagos: number;
  titulosAtrasados12Meses: number;
  clienteHaAnos: number;
  clienteHaMeses: number;
  faturamentoMeses: FaturamentoMes[];
  prazoPagamento: {
    vista: string;
    prazo30: string;
    prazo60: string;
    prazo90: string;
  };
  combustiveis: {
    gasolina: { quantidade: string; preco: string };
    diesel: { quantidade: string; preco: string };
    dieselS10: { quantidade: string; preco: string };
    etanol: { quantidade: string; preco: string };
    gas: { quantidade: string; preco: string};
  };
}

export const DeclaracaoFaturamento: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    codigoCliente: '',
    codigoGrupoEconomico: '',
    razaoSocial: '',
    cnpjCpf: '',
    inscricaoEstadual: '',
    tipoPropriedade: '',
    tempoAtividade: '',
    codigoAssessor: '',
    nomeAssessor: '',
    ultimaCompra: '',
    compraTotal: 0,
    titulosPagos: 0,
    titulosAtrasados12Meses: 0,
    clienteHaAnos: 0,
    clienteHaMeses: 0,
    faturamentoMeses: Array.from({ length: 12 }, (_, i) => ({
      mes: '',
      ano: '',
      faturamento: ''
    })),
    prazoPagamento: {
      vista: '',
      prazo30: '',
      prazo60: '',
      prazo90: ''
    },
    combustiveis: {
      gasolina: { quantidade: '', preco: '' },
      diesel: { quantidade: '', preco: '' },
      dieselS10: { quantidade: '', preco: '' },
      etanol: { quantidade: '', preco: '' },
      gas: { quantidade: '', preco: ''}
    }
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loadingModal, setLoadingModal] = useState<boolean>(false);
  const [messagesLoading, setMessagesLoading] = useState<string>("");
  const [errorApi, setErrorApi] = useState({
    message: "",
    title: "",
    state: false,
  });
  
  // Ref para controlar o timeout do debounce
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Função para consultar dados básicos do cliente
  const consultarDadosBasicos = async (cnpj: string, codigoGrupoEconomico?: string) => {
    if (!cnpj && !codigoGrupoEconomico) return;
    
    setLoadingModal(true);
    setMessagesLoading("Consultando dados básicos do cliente...");
    setErrorApi({ message: "", title: "", state: false });

    try {
      const config = {
        headers: headers(),
      };

      // Constrói a query string baseada nos parâmetros fornecidos
      const params = new URLSearchParams();
      if (cnpj) params.append('cnpj', cnpj);
      if (codigoGrupoEconomico) params.append('codigoAssessor', codigoGrupoEconomico);

      const { data } = await apiPetro.get<Resultado<DadosBasicosCliente>>(
        `analise/consultar-dados-basicos?${params.toString()}`, 
        config
      );

      if (!data.erro && data.valor) {
        const dadosCliente = data.valor;
        
        // Preenche os campos do formulário com os dados retornados
        setFormData(prev => {
          const newFormData = {
            ...prev,
            codigoCliente: dadosCliente.codigo || '',
            cnpjCpf: formatCnpjCpf(dadosCliente.cnpj || ''),
            razaoSocial: dadosCliente.razaoSocial || '',
            inscricaoEstadual: dadosCliente.inscricaoEstadual || '',
            codigoGrupoEconomico: dadosCliente.codigo_assessor || '', // Este é o código do assessor
            codigoAssessor: dadosCliente.codigo_assessor || '', // Mantendo também no assessor por compatibilidade
            nomeAssessor: dadosCliente.nomeAssessor || '',
            ultimaCompra: formatDateToBR(dadosCliente.ultimaCompra || ''),
            compraTotal: dadosCliente.compraTotal || 0,
            titulosPagos: dadosCliente.titulosPagos || 0,
            titulosAtrasados12Meses: dadosCliente.titulosAtrasados12Meses || 0,
            clienteHaAnos: dadosCliente.clienteHaAnos || 0,
            clienteHaMeses: dadosCliente.clienteHaMeses || 0,
          };
          
          return newFormData;
        });
        
        // Verificação adicional após um pequeno delay
        setTimeout(() => {
        }, 100);
        
      } else {
        setErrorApi({
          state: true,
          message: data.erro ? data.erros[0] || "Dados do cliente não encontrados." : "Dados do cliente não encontrados.",
          title: "Erro na Consulta",
        });
      }
    } catch (error: any) {
      setErrorApi({
        state: true,
        message: error.response?.data?.message || "Erro ao consultar dados básicos do cliente.",
        title: "Erro",
      });
    } finally {
      setLoadingModal(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    // Define quais campos são readonly e não devem ser alterados manualmente
    const readonlyFields = ['codigoCliente', 'razaoSocial', 'inscricaoEstadual', 'codigoAssessor', 'nomeAssessor', 'ultimaCompra', 'compraTotal', 'titulosPagos', 'titulosAtrasados12Meses', 'clienteHaAnos', 'clienteHaMeses'];
    
    // Se é um campo readonly, não faz nada (evita sobrescrita acidental)
    if (readonlyFields.includes(field)) {
      return;
    }
    
    // Define quais campos são numéricos
    const numericFields = ['compraTotal', 'titulosPagos', 'titulosAtrasados12Meses', 'clienteHaAnos', 'clienteHaMeses'];
    
    let processedValue: any = value;
    
    // Aplica formatação especial para CNPJ/CPF
    if (field === 'cnpjCpf') {
      // Remove caracteres não numéricos para verificar o comprimento
      const numbersOnly = value.replace(/\D/g, '');
      
      // Limita a entrada a 14 dígitos (CNPJ)
      const limitedNumbers = numbersOnly.slice(0, 14);
      
      // Aplica a formatação
      processedValue = formatCnpjCpf(limitedNumbers);
      
      // Limpa o timeout anterior se existir
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      
      // Configura um novo timeout para disparar a consulta após 2 segundos de inatividade
      if (limitedNumbers.length === 11 || limitedNumbers.length === 14) {
        debounceTimeoutRef.current = setTimeout(() => {
          consultarDadosBasicos(limitedNumbers);
        }, 2000); // 2 segundos de delay
      }
    } else if (numericFields.includes(field)) {
      processedValue = Number(value) || 0;
    }
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: processedValue
      };
      return updated;
    });
  };

  const handleFaturamentoChange = (index: number, field: keyof FaturamentoMes, value: string) => {
    setFormData(prev => ({
      ...prev,
      faturamentoMeses: prev.faturamentoMeses.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  // Função para gerar os próximos 11 meses a partir de uma data inicial
  const generateSubsequentMonths = (startMonth: number, startYear: number) => {
    const months = [];
    let currentMonth = startMonth;
    let currentYear = startYear;
    
    for (let i = 0; i < 12; i++) {
      months.push({
        mes: currentMonth.toString().padStart(2, '0'),
        ano: currentYear.toString(),
        faturamento: ''
      });
      
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }
    
    return months;
  };

  // Função modificada para preencher automaticamente os meses subsequentes
  const handleDateChangeWithAutoFill = (index: number, formattedValue: string) => {
    const parts = formattedValue.split('/');
    const mes = parts[0] || '';
    const ano = parts[1] || '';
    
    setFormData(prev => {
      const newFaturamentoMeses = [...prev.faturamentoMeses];
      
      // Se é o primeiro campo (index 0) e tem mês e ano completos
      if (index === 0 && mes.length === 2 && ano.length === 4) {
        const startMonth = parseInt(mes);
        const startYear = parseInt(ano);
        
        // Gera os 12 meses subsequentes
        const generatedMonths = generateSubsequentMonths(startMonth, startYear);
        
        // Atualiza todos os campos, preservando o faturamento já preenchido
        generatedMonths.forEach((generatedMonth, i) => {
          if (i < newFaturamentoMeses.length) {
            newFaturamentoMeses[i] = {
              ...newFaturamentoMeses[i],
              mes: generatedMonth.mes,
              ano: generatedMonth.ano
            };
          }
        });
      } else {
        // Para outros índices, atualiza apenas o campo específico
        newFaturamentoMeses[index] = {
          ...newFaturamentoMeses[index],
          mes: mes,
          ano: ano
        };
      }
      
      return {
        ...prev,
        faturamentoMeses: newFaturamentoMeses
      };
    });
  };

  const handlePrazoPagamentoChange = (field: keyof typeof formData.prazoPagamento, value: string) => {
    setFormData(prev => ({
      ...prev,
      prazoPagamento: {
        ...prev.prazoPagamento,
        [field]: value
      }
    }));
  };

  const handleCombustivelChange = (
    combustivel: keyof typeof formData.combustiveis, 
    field: 'quantidade' | 'preco', 
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      combustiveis: {
        ...prev.combustiveis,
        [combustivel]: {
          ...prev.combustiveis[combustivel],
          [field]: value
        }
      }
    }));
  };

  const formatCurrency = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    const formattedValue = (Number(numericValue) / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return formattedValue;
  };

  // Função específica para formatar preço por litro com 4 casas decimais
  const formatPricePerLiter = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    const formattedValue = (Number(numericValue) / 10000).toLocaleString('pt-BR', {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4
    });
    return formattedValue;
  };

  // Função para formatar Mês/Ano
  const formatMesAno = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 6) {
      return numbers.replace(/(\d{2})(\d{0,4})/, '$1/$2');
    }
    
    // Limita a 6 dígitos (MMAAAA)
    return numbers.substring(0, 6).replace(/(\d{2})(\d{4})/, '$1/$2');
  };

  // Função para formatar CNPJ/CPF
  const formatCnpjCpf = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    
    if (numbers.length <= 11) {
      // CPF: 000.000.000-00
      if (numbers.length <= 3) {
        return numbers;
      } else if (numbers.length <= 6) {
        return numbers.replace(/(\d{3})(\d{1,3})/, '$1.$2');
      } else if (numbers.length <= 9) {
        return numbers.replace(/(\d{3})(\d{3})(\d{1,3})/, '$1.$2.$3');
      } else {
        return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{1,2})/, '$1.$2.$3-$4');
      }
    } else {
      // CNPJ: 00.000.000/0000-00
      if (numbers.length <= 2) {
        return numbers;
      } else if (numbers.length <= 5) {
        return numbers.replace(/(\d{2})(\d{1,3})/, '$1.$2');
      } else if (numbers.length <= 8) {
        return numbers.replace(/(\d{2})(\d{3})(\d{1,3})/, '$1.$2.$3');
      } else if (numbers.length <= 12) {
        return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{1,4})/, '$1.$2.$3/$4');
      } else {
        return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{1,2})/, '$1.$2.$3/$4-$5');
      }
    }
  };

  // Usamos a função utilitária para formatar datas
  // const formatDateBR = formatDateToBR; // Já importada

  // Função para formatar valor monetário
  const formatMoney = (value: number) => {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  };

  const handleCurrencyInput = (e: React.ChangeEvent<HTMLInputElement>, onChange: (value: string) => void) => {
    const formatted = formatCurrency(e.target.value);
    onChange(formatted);
  };

  // Função específica para inputs de preço por litro (4 casas decimais)
  const handlePricePerLiterInput = (e: React.ChangeEvent<HTMLInputElement>, onChange: (value: string) => void) => {
    const formatted = formatPricePerLiter(e.target.value);
    onChange(formatted);
  };

  // Função para prevenir envio do formulário ao pressionar Enter
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoadingModal(true);
    setMessagesLoading("Enviando declaração de faturamento...");

    try {
      // Preparar os dados para envio conforme a estrutura da API
      const dataToSend = {
        codigoCliente: formData.codigoCliente,
        razaoSocial: formData.razaoSocial,
        cnpjOuCpf: formData.cnpjCpf.replace(/\D/g, ''), // Remove formatação do CNPJ/CPF
        inscricaoEstadual: formData.inscricaoEstadual,
        nomeAssessor: formData.nomeAssessor,
        codigoAssessor: formData.codigoAssessor,
        ultimaCompra: formData.ultimaCompra,
        compraTotal: formData.compraTotal,
        titulosPagos: formData.titulosPagos,
        titulosAtrasados12Meses: formData.titulosAtrasados12Meses,
        clienteAnos: formData.clienteHaAnos, // Mapeando para o nome correto da API
        clienteMeses: formData.clienteHaMeses, // Mapeando para o nome correto da API
        tipoPropriedade: formData.tipoPropriedade,
        tempoAtividade: formData.tempoAtividade,
        prazoPagamento: {
          vista: parseFloat(formData.prazoPagamento.vista) || 0,
          prazo30: parseFloat(formData.prazoPagamento.prazo30) || 0,
          prazo60: parseFloat(formData.prazoPagamento.prazo60) || 0,
          prazo90: parseFloat(formData.prazoPagamento.prazo90) || 0
        },
        faturamentoMeses: formData.faturamentoMeses
          .filter(item => item.mes && item.ano && item.faturamento) // Filtra apenas os meses preenchidos
          .map(item => ({
            mes: `${item.mes}/${item.ano}`, 
            valor: parseFloat(item.faturamento.replace(/\./g, '').replace(',', '.')) || 0
          })),
        combustiveis: {
          gasolina: {
            quantidade: parseFloat(formData.combustiveis.gasolina.quantidade) || 0,
            preco: parseFloat(formData.combustiveis.gasolina.preco.replace(/\./g, '').replace(',', '.')) || 0
          },
          diesel: {
            quantidade: parseFloat(formData.combustiveis.diesel.quantidade) || 0,
            preco: parseFloat(formData.combustiveis.diesel.preco.replace(/\./g, '').replace(',', '.')) || 0
          },
          dieselS10: {
            quantidade: parseFloat(formData.combustiveis.dieselS10.quantidade) || 0,
            preco: parseFloat(formData.combustiveis.dieselS10.preco.replace(/\./g, '').replace(',', '.')) || 0
          },
          etanol: {
            quantidade: parseFloat(formData.combustiveis.etanol.quantidade) || 0,
            preco: parseFloat(formData.combustiveis.etanol.preco.replace(/\./g, '').replace(',', '.')) || 0
          },
          gas: {
            quantidade: parseFloat(formData.combustiveis.gas.quantidade) || 0,
            preco: parseFloat(formData.combustiveis.gas.preco.replace(/\./g, '').replace(',', '.')) || 0 
          }
        }
      };

      // TODO: Substituir pela URL correta do backend quando disponível
      const url = 'analise/declaracao-faturamento'; // Substitua pela URL correta
      
      const config = {
        headers: headers(),
      };

      const response = await apiPetro.post<Resultado<any>>('analise/declaracao-faturamento', dataToSend, config);

      setLoadingModal(false);

      if (response.data.erro) {
        setErrorApi({
          state: true,
          message: response.data.erros.join(", "),
          title: "Erro ao enviar declaração",
        });
        return;
      }

      // Sucesso
      alert('Declaração enviada com sucesso!');
      
      // Limpar o formulário após envio bem-sucedido
      resetForm();
      
    } catch (error: any) {
      setLoadingModal(false);
      setMessagesLoading("");
      setErrorApi({
        state: true,
        message: error.response?.data?.message || "Erro ao enviar declaração.",
        title: "Erro",
      });
    }
  };

  // Função auxiliar para limpar o formulário (opcional)
  const resetForm = () => {
    setFormData({
      codigoCliente: '',
      codigoGrupoEconomico: '',
      razaoSocial: '',
      cnpjCpf: '',
      inscricaoEstadual: '',
      tipoPropriedade: '',
      tempoAtividade: '',
      codigoAssessor: '',
      nomeAssessor: '',
      ultimaCompra: '',
      compraTotal: 0,
      titulosPagos: 0,
      titulosAtrasados12Meses: 0,
      clienteHaAnos: 0,
      clienteHaMeses: 0,
      faturamentoMeses: Array.from({ length: 12 }, () => ({
        mes: '',
        ano: '',
        faturamento: ''
      })),
      prazoPagamento: {
        vista: '',
        prazo30: '',
        prazo60: '',
        prazo90: ''
      },
      combustiveis: {
        gasolina: { quantidade: '', preco: '' },
        diesel: { quantidade: '', preco: '' },
        dieselS10: { quantidade: '', preco: '' },
        etanol: { quantidade: '', preco: '' },
        gas: { quantidade: '', preco: ''}
      }
    });
  };

  useEffect(() => {
    // Inicializa com campos vazios para que o usuário possa preencher
    setFormData(prev => ({
      ...prev,
      faturamentoMeses: Array.from({ length: 12 }, () => ({
        mes: '',
        ano: '',
        faturamento: ''
      }))
    }));
  }, []);

  // Limpa o timeout quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);


  return (
    <>
      <Header />
      <Loading
        mode="blurry"
        hidden={!loadingModal}
        messages={messagesLoading}
      />
      <S.Container>
        <S.Header>
          <S.Title>Declaração de Faturamento dos Últimos 12 Meses</S.Title>
        </S.Header>

        <S.FormContainer onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
        <S.Section>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <S.InputGroup style={{ flex: '1', minWidth: '200px' }}>
              <S.Label>Código Cliente</S.Label>
              <S.Input
                type="text"
                value={formData.codigoCliente}
                placeholder="Digite o código do cliente"
                readOnly
              />
            </S.InputGroup>

            <S.InputGroup style={{ flex: '1', minWidth: '300px' }}>
              <S.Label>CNPJ/CPF</S.Label>
              <S.Input
                type="text"
                value={formData.cnpjCpf}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('cnpjCpf', e.target.value)}
                placeholder="Digite o CNPJ ou CPF"
              />
            </S.InputGroup>
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <S.InputGroup style={{ flex: '2', minWidth: '400px' }}>
              <S.Label>Razão Social</S.Label>
              <S.Input
                type="text"
                value={formData.razaoSocial}
                placeholder="Digite a razão social"
                readOnly
              />
            </S.InputGroup>

            <S.InputGroup style={{ flex: '1', minWidth: '300px' }}>
              <S.Label>Inscrição Estadual</S.Label>
              <S.Input
                type="text"
                value={formData.inscricaoEstadual}
                placeholder="Digite a inscrição estadual"
                readOnly
              />
            </S.InputGroup>
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <S.InputGroup style={{ flex: '1', minWidth: '200px' }}>
              <S.Label>Código Assessor</S.Label>
              <S.Input
                type="text"
                value={formData.codigoAssessor}
                placeholder="Código do assessor"
                readOnly
              />
            </S.InputGroup>

            <S.InputGroup style={{ flex: '1', minWidth: '300px' }}>
              <S.Label>Nome Assessor</S.Label>
              <S.Input
                type="text"
                value={formData.nomeAssessor}
                placeholder="Nome do assessor"
                readOnly
              />
            </S.InputGroup>
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <S.InputGroup style={{ flex: '1', minWidth: '200px' }}>
              <S.Label>Última Compra</S.Label>
              <S.Input
                type="text"
                value={formData.ultimaCompra}
                placeholder="Data da última compra"
                readOnly
              />
            </S.InputGroup>

            <S.InputGroup style={{ flex: '1', minWidth: '200px' }}>
              <S.Label>Compra Total</S.Label>
              <S.Input
                type="text"
                value={formData.compraTotal ? formatMoney(formData.compraTotal) : ''}
                placeholder="Valor total de compras"
                readOnly
              />
            </S.InputGroup>

            <S.InputGroup style={{ flex: '1', minWidth: '200px' }}>
              <S.Label>Títulos Pagos</S.Label>
              <S.Input
                type="number"
                value={formData.titulosPagos}
                placeholder="Quantidade de títulos pagos"
                readOnly
              />
            </S.InputGroup>

            <S.InputGroup style={{ flex: '1', minWidth: '200px' }}>
              <S.Label>Títulos Atrasados (12 meses)</S.Label>
              <S.Input
                type="number"
                value={formData.titulosAtrasados12Meses}
                placeholder="Títulos atrasados"
                readOnly
              />
            </S.InputGroup>
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <S.InputGroup style={{ flex: '1', minWidth: '200px' }}>
              <S.Label>Cliente há (anos)</S.Label>
              <S.Input
                type="number"
                value={formData.clienteHaAnos}
                placeholder="Anos como cliente"
                readOnly
              />
            </S.InputGroup>

            <S.InputGroup style={{ flex: '1', minWidth: '200px' }}>
              <S.Label>Cliente há (meses)</S.Label>
              <S.Input
                type="number"
                value={formData.clienteHaMeses}
                placeholder="Meses como cliente"
                readOnly
              />
            </S.InputGroup>
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <S.InputGroup style={{ flex: '1', minWidth: '200px' }}>
              <S.Label>Tipo de Propriedade</S.Label>
              <S.Select
                value={formData.tipoPropriedade}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleInputChange('tipoPropriedade', e.target.value)}
              >
                <option value="">Selecione...</option>
                <option value="Própria">Própria</option>
                <option value="Arrendada">Arrendada</option>
              </S.Select>
            </S.InputGroup>

            <S.InputGroup style={{ flex: '1', minWidth: '200px' }}>
              <S.Label>
                <S.Required>*</S.Required>
                Tempo de Atividade (meses)
              </S.Label>
              <S.Input
                type="text"
                value={formData.tempoAtividade}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleInputChange('tempoAtividade', e.target.value)}
                placeholder="Digite o tempo de atividade"
              />
              <S.Note>
                <S.Required>*</S.Required>
                Preencher se for pessoa física.
              </S.Note>
            </S.InputGroup>
          </div>
        </S.Section>

        <S.Section>
          <S.SectionTitle>Faturamento dos Últimos 12 Meses</S.SectionTitle>
          <S.Note>
            <S.Required>*</S.Required>
            Por favor utilize o padrão monetário brasileiro, ex.: 10.000,00
          </S.Note>
          
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            {/* Primeira coluna - 6 primeiros meses */}
            <div style={{ flex: '1', minWidth: '300px' }}>
              <S.Table>
                <S.TableHeader>
                  <S.TableRow>
                    <S.TableHeaderCell width="10%"></S.TableHeaderCell>
                    <S.TableHeaderCell width="45%">Mês/Ano</S.TableHeaderCell>
                    <S.TableHeaderCell width="45%">Faturamento (R$)</S.TableHeaderCell>
                  </S.TableRow>
                </S.TableHeader>
                <S.TableBody>
                  {formData.faturamentoMeses.slice(0, 6).map((item, index) => (
                    <S.TableRow key={index}>
                      <S.TableCell>{index + 1}.</S.TableCell>
                      <S.TableCell>
                        <S.TableInput
                          type="text"
                          value={`${item.mes}${item.mes && item.ano ? '/' : ''}${item.ano}`}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const formattedValue = formatMesAno(e.target.value);
                            handleDateChangeWithAutoFill(index, formattedValue);
                          }}
                          placeholder="MM/AAAA"
                          maxLength={7}
                        />
                      </S.TableCell>
                      <S.TableCell>
                        <S.TableInput
                          type="text"
                          value={item.faturamento}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCurrencyInput(e, (value) => 
                            handleFaturamentoChange(index, 'faturamento', value)
                          )}
                          placeholder="0,00"
                        />
                      </S.TableCell>
                    </S.TableRow>
                  ))}
                </S.TableBody>
              </S.Table>
            </div>

            {/* Segunda coluna - 6 últimos meses */}
            <div style={{ flex: '1', minWidth: '300px' }}>
              <S.Table>
                <S.TableHeader>
                  <S.TableRow>
                    <S.TableHeaderCell width="10%"></S.TableHeaderCell>
                    <S.TableHeaderCell width="45%">Mês/Ano</S.TableHeaderCell>
                    <S.TableHeaderCell width="45%">Faturamento (R$)</S.TableHeaderCell>
                  </S.TableRow>
                </S.TableHeader>
                <S.TableBody>
                  {formData.faturamentoMeses.slice(6, 12).map((item, index) => (
                    <S.TableRow key={index + 6}>
                      <S.TableCell>{index + 7}.</S.TableCell>
                      <S.TableCell>
                        <S.TableInput
                          type="text"
                          value={`${item.mes}${item.mes && item.ano ? '/' : ''}${item.ano}`}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const formattedValue = formatMesAno(e.target.value);
                            const parts = formattedValue.split('/');
                            const mes = parts[0] || '';
                            const ano = parts[1] || '';
                            
                            // Atualiza apenas o campo específico (não faz auto-preenchimento)
                            setFormData(prev => ({
                              ...prev,
                              faturamentoMeses: prev.faturamentoMeses.map((item, i) => 
                                i === (index + 6) ? { ...item, mes: mes, ano: ano } : item
                              )
                            }));
                          }}
                          placeholder="MM/AAAA"
                          maxLength={7}
                        />
                      </S.TableCell>
                      <S.TableCell>
                        <S.TableInput
                          type="text"
                          value={item.faturamento}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCurrencyInput(e, (value) => 
                            handleFaturamentoChange(index + 6, 'faturamento', value)
                          )}
                          placeholder="0,00"
                        />
                      </S.TableCell>
                    </S.TableRow>
                  ))}
                </S.TableBody>
              </S.Table>
            </div>
          </div>
        </S.Section>

        <S.Section>
          <S.SectionTitle>Prazo para Pagamento (%)</S.SectionTitle>
          <S.Table>
            <S.TableHeader>
              <S.TableRow>
                <S.TableHeaderCell>À Vista</S.TableHeaderCell>
                <S.TableHeaderCell>30 dias</S.TableHeaderCell>
                <S.TableHeaderCell>60 dias</S.TableHeaderCell>
                <S.TableHeaderCell>90 dias</S.TableHeaderCell>
              </S.TableRow>
            </S.TableHeader>
            <S.TableBody>
              <S.TableRow>
                <S.TableCell>
                  <S.TableInput
                    type="text"
                    value={formData.prazoPagamento.vista}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePrazoPagamentoChange('vista', e.target.value)}
                    placeholder="%"
                  />
                </S.TableCell>
                <S.TableCell>
                  <S.TableInput
                    type="text"
                    value={formData.prazoPagamento.prazo30}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePrazoPagamentoChange('prazo30', e.target.value)}
                    placeholder="%"
                  />
                </S.TableCell>
                <S.TableCell>
                  <S.TableInput
                    type="text"
                    value={formData.prazoPagamento.prazo60}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePrazoPagamentoChange('prazo60', e.target.value)}
                    placeholder="%"
                  />
                </S.TableCell>
                <S.TableCell>
                  <S.TableInput
                    type="text"
                    value={formData.prazoPagamento.prazo90}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePrazoPagamentoChange('prazo90', e.target.value)}
                    placeholder="%"
                  />
                </S.TableCell>
              </S.TableRow>
            </S.TableBody>
          </S.Table>
        </S.Section>

        <S.Section>
          <S.SectionTitle>Quantidade Média Mensal e Preço por Litro</S.SectionTitle>
          <S.Table>
            <S.TableHeader>
              <S.TableRow>
                <S.TableHeaderCell width="33%">Combustível</S.TableHeaderCell>
                <S.TableHeaderCell width="33%">Quantidade (L)</S.TableHeaderCell>
                <S.TableHeaderCell width="33%">Preço por Litro (R$)</S.TableHeaderCell>
              </S.TableRow>
            </S.TableHeader>
            <S.TableBody>
              {Object.entries(formData.combustiveis).map(([combustivel, data]) => {
                // Mapeia os nomes dos combustíveis
                const nomesCombustiveis: { [key: string]: string } = {
                  gasolina: 'Gasolina',
                  diesel: 'Diesel',
                  dieselS10: 'Diesel S10',
                  etanol: 'Etanol',
                  gas: 'Gás'
                };

                return (
                  <S.TableRow key={combustivel}>
                    <S.TableCell>
                      {nomesCombustiveis[combustivel] || combustivel}
                    </S.TableCell>
                    <S.TableCell>
                      <S.TableInput
                        type="text"
                        value={data.quantidade}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleCombustivelChange(
                          combustivel as keyof typeof formData.combustiveis, 
                          'quantidade', 
                          e.target.value
                        )}
                        placeholder="0"
                      />
                    </S.TableCell>
                    <S.TableCell>
                      <S.TableInput
                        type="text"
                        value={data.preco}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handlePricePerLiterInput(e, (value) => 
                          handleCombustivelChange(
                            combustivel as keyof typeof formData.combustiveis, 
                            'preco', 
                            value
                          )
                        )}
                        placeholder="0,0000"
                      />
                    </S.TableCell>
                  </S.TableRow>
                );
              })}
            </S.TableBody>
          </S.Table>
        </S.Section>

        <S.ButtonContainer>
          <S.SubmitButton type="submit" disabled={loadingModal}>
            {loadingModal ? 'Enviando...' : 'Enviar Declaração'}
          </S.SubmitButton>
        </S.ButtonContainer>
      </S.FormContainer>
    </S.Container>
    
    <Error
      hasOpen={errorApi.state}
      handleClose={() => setErrorApi({ ...errorApi, state: false })}
      errorMsg={errorApi.message}
      title={errorApi.title}
    />
    </>
  );
};
